import AuthModule from '@modules/AuthModule';
import useAuth from '@modules/AuthModule/hooks/useAuth';
import {
  HELP_CENTER_BUTTON_STORE,
  HELP_CENTER_STORE,
  HELP_CENTER_TOPIC_STORE,
  HelpCenterLoader,
  IHelpCenterSettings,
} from '@servicetitan/help-center';
import { useDependencies } from '@servicetitan/react-ioc';
import { observer } from 'mobx-react';
import { useEffect, useMemo } from 'react';
import environment from '@lib/environment';
import { FeatureFlagKeys, useFeatureFlags } from '@modules/FeatureFlagModule';

const { API_URL, HELP_CENTER_URL } = environment;

const AUTH_URL = API_URL + '/v1/help-center/auth';

export const HelpCenterMfeLoader = observer(() => {
  const { [FeatureFlagKeys.featureHelpCenter]: featureHelpCenter } =
    useFeatureFlags();

  const [helpCenterStore, helpCenterButtonStore, helpCenterTopicStore] =
    useDependencies(
      HELP_CENTER_STORE,
      HELP_CENTER_BUTTON_STORE,
      HELP_CENTER_TOPIC_STORE,
    );

  useEffect(() => {
    helpCenterButtonStore.setLabel('Help Center.');
  }, [helpCenterButtonStore]);

  // alwaysFresh: this widget calls /v1/help-center/auth directly via fetch,
  // not through Apollo, so nothing keeps `token` current except this hook's
  // own 60s interval (and the immediate priming call below). Reading `token`
  // from the snapshot (rather than copying it into local state once) means
  // authUrlHeaders recomputes automatically every time that interval — or any
  // other getCurrentToken() caller — refreshes it.
  const { user, signedIn, token } = useAuth(true);

  // getCurrentToken() fetches a bridge JWT from /bff/token and writes it
  // into authState.token. This priming call exists only to populate `token`
  // immediately on sign-in — useAuth's interval above doesn't fire until
  // its first 60s tick, and `token` starts out empty until something calls
  // getCurrentToken().
  //
  // Errors are swallowed here rather than gating on a one-shot "primed"
  // flag: a transient /bff/token failure would otherwise leave the loader
  // permanently unmounted for the rest of the session. Gating on `token`
  // itself below means recovery is automatic — useAuth's 60s interval keeps
  // retrying, and the loader mounts as soon as one of those calls succeeds.
  useEffect(() => {
    if (!signedIn) {
      return;
    }
    AuthModule.getCurrentToken().catch(() => {});
  }, [signedIn]);

  const settings = useMemo(
    (): IHelpCenterSettings => ({
      initialScreen: helpCenterStore.initialScreen,
      isOpen: helpCenterButtonStore.isOpen,
      location: helpCenterTopicStore.location?.pathname ?? '',
      serviceUrl: HELP_CENTER_URL,
      authUrl: AUTH_URL,
      instance: 'CCPro',
      tenantName: '',
      userName: user?.email ?? '',
      name: user?.name ?? '',
      phone: '',
      email: user?.email ?? '',
      mobilePhone: '',
      topic: '',
      authUrlHeaders: { Authorization: `Bearer ${token}` },
      tenantIds: user?.org.tenants.map((t) => Number(t.id)) ?? [],
    }),
    [
      helpCenterStore.initialScreen,
      helpCenterButtonStore.isOpen,
      helpCenterTopicStore.location?.pathname,
      user,
      token,
    ],
  );

  if (!featureHelpCenter || !signedIn || !token) {
    return null;
  }

  return (
    <div>
      <HelpCenterLoader settings={settings} />
    </div>
  );
});
